import { motion } from "framer-motion";
import {
  ShieldCheck,
  Moon,
  RefreshCw,
  ScrollText,
  MapPinned,
  Check,
  ShoppingCart,
} from "lucide-react";
import { Reveal } from "../lib/anim";

const DASHCAM_BENEFITS = [
  {
    icon: ShieldCheck,
    title: "Accident & insurance proof",
    desc: "Undeniable HD video evidence protects you from false claims and speeds up insurance settlements.",
  },
  {
    icon: Moon,
    title: "Crystal-clear night vision",
    desc: "Wide-dynamic-range sensor captures sharp footage in low light, tunnels and glare.",
  },
  {
    icon: RefreshCw,
    title: "24/7 loop + parking guard",
    desc: "Seamless loop recording with G-sensor auto-lock and motion-triggered parking mode.",
  },
  {
    icon: ScrollText,
    title: "Fights fraud & road rage",
    desc: "A visible dashcam deters staged accidents, disputes and aggressive drivers.",
  },
];

export const BuyDVR = ({ onBuy }) => {
  return (
    <section
      id="buy-dvr"
      data-testid="buy-dvr-section"
      className="relative py-24 md:py-32 bg-black border-y border-white/10"
    >
      <div className="absolute top-20 left-1/2 -translate-x-1/2 h-72 w-[70%] rounded-full bg-[#00B4D8]/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left: heading + intro above the camera pic */}
        <div>
          <Reveal>
            <p className="font-mono-gx text-xs uppercase tracking-[0.25em] text-[#00B4D8] mb-4">
              / Buy Car DVR
            </p>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.1]">
              The GeoNetix{" "}
              <span className="gx-text-gradient">Dashcam.</span>
            </h2>
            <p className="mt-5 text-slate-400 max-w-xl leading-relaxed">
              One device, two jobs: protect every drive and turn it into income.
              A premium car DVR that safeguards you on the road — and powers the
              GeoNetix mapping network.
            </p>
          </Reveal>

          {/* Product visual */}
          <Reveal delay={0.1}>
            <div className="relative mt-8">
              <div className="absolute inset-0 m-auto h-3/4 w-3/4 rounded-full bg-[#00B4D8]/20 blur-[80px]" />
              <motion.img
                src="/car-dvr.png"
                alt="GeoNetix approved Car DVR dashcam"
                data-testid="buy-dvr-image"
                className="relative w-full max-w-md mx-auto rounded-3xl"
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              />
              <div className="relative mt-6 flex flex-wrap justify-center gap-3">
                {["4K UHD", "GPS-tagged", "Wi-Fi Sync", "GeoNetix-Approved"].map((t) => (
                  <span
                    key={t}
                    className="rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs text-slate-300 font-mono-gx"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        {/* Right: benefits, road mapping, price + CTA */}
        <div>
          {/* Dashcam benefits grid */}
          <div className="grid sm:grid-cols-2 gap-4">
            {DASHCAM_BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={i * 0.06}>
                  <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-5 transition-colors duration-300 hover:border-[#00B4D8]/40">
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#00B4D8]/10 border border-[#00B4D8]/30">
                      <Icon size={18} className="text-[#00B4D8]" />
                    </div>
                    <h3 className="font-semibold text-white text-[15px] mb-1.5">
                      {b.title}
                    </h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{b.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Additional benefit: Road Mapping */}
          <Reveal delay={0.1}>
            <div
              data-testid="road-mapping-benefit"
              className="mt-6 rounded-2xl border border-[#4CAF50]/30 bg-gradient-to-r from-[#4CAF50]/10 to-transparent p-6"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#4CAF50]/15 border border-[#4CAF50]/40">
                  <MapPinned size={20} className="text-[#4CAF50]" />
                </div>
                <div>
                  <div className="font-mono-gx text-[11px] uppercase tracking-[0.2em] text-[#4CAF50] mb-1">
                    The GeoNetix Bonus
                  </div>
                  <h3 className="font-semibold text-white text-base mb-2">
                    Road Mapping that pays you back
                  </h3>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    Unlike an ordinary dashcam, this one earns. It automatically
                    captures geolocated, privacy-scrubbed road imagery as you drive
                    — feeding the DePIN network and paying you up to{" "}
                    <span className="font-semibold text-[#4CAF50]">&#8377;2.00/km</span>{" "}
                    in real INR. Your safety gear becomes a passive income engine.
                  </p>
                  <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                    {["Passive income while you drive", "Faces & plates auto-blurred", "Payouts in INR to your bank"].map((p) => (
                      <li key={p} className="flex items-center gap-1.5 text-xs text-slate-300">
                        <Check size={13} className="text-[#4CAF50]" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Price + CTA */}
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5">
              <div>
                <div className="flex items-end gap-2 flex-wrap">
                  <span className="font-display font-black text-3xl text-white">&#8377;15,930</span>
                  <span className="text-slate-500 line-through text-sm mb-1">&#8377;21,240</span>
                  <span className="mb-1 rounded-full bg-[#4CAF50]/15 border border-[#4CAF50]/40 px-2.5 py-0.5 text-xs font-semibold text-[#4CAF50]">25% OFF</span>
                </div>
                <div className="text-xs text-slate-400 mt-1">Including all taxes</div>
              </div>
              <button
                data-testid="buy-dvr-cta"
                onClick={onBuy}
                className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-[#4CAF50] px-8 py-4 text-[15px] font-semibold text-[#04140a] hover:brightness-110 transition-[filter,transform] duration-200 hover:-translate-y-0.5 gx-green-glow"
              >
                <ShoppingCart size={18} className="transition-transform duration-200 group-hover:-translate-y-0.5" />
                Buy Car DVR
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
